import { Modal, Form, DatePicker, Select, message } from "antd";
import { useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useAssignableAuditors } from "./useAssignableAuditors";

interface RouteEditModalProps {
  visible: boolean;
  onClose: () => void;
  route: {
    id: number;
    route_date?: string | null;
    auditor?: number | null;
    auditor_name?: string | null;
  } | null;
  auditors: Array<{ id: number; name: string }>;
  auditCompanyId?: number | null;
  countryId?: number | null;
  isAuditorOnly?: boolean;
  currentUser?: { id: number; name: string } | null;
  onSuccess?: () => void;
}

interface RouteEditFormValues {
  route_date: dayjs.Dayjs | null;
  auditor: number | null;
}

/**
 * RouteEditModal — change `route_date` and `auditor` of an existing
 * RoutePerformed entry. The GPX file is left untouched (use upload modal
 * to replace it; the dedicated endpoint for that is out of scope here).
 */
export const RouteEditModal = ({
  visible,
  onClose,
  route,
  auditors,
  auditCompanyId,
  countryId,
  isAuditorOnly = false,
  currentUser,
  onSuccess,
}: RouteEditModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RouteEditFormValues>();
  const { mutate: updateRoute } = useUpdate();
  const [submitting, setSubmitting] = useState(false);

  const auditorPicker = useAssignableAuditors({
    visible,
    auditCompanyId,
    countryId,
    isAuditorOnly,
    currentUser,
    fallback: auditors,
  });

  useEffect(() => {
    if (visible && route) {
      form.setFieldsValue({
        route_date: route.route_date ? dayjs(route.route_date) : null,
        // When locked to the current user, force them as the value even
        // if the route was originally assigned to someone else — they
        // can only re-attribute it to themselves.
        auditor: auditorPicker.locked
          ? auditorPicker.defaultValue ?? null
          : route.auditor ?? null,
      });
    } else if (!visible) {
      form.resetFields();
    }
  }, [
    visible,
    route,
    form,
    auditorPicker.locked,
    auditorPicker.defaultValue,
  ]);

  const handleSubmit = (values: RouteEditFormValues) => {
    if (!route) return;
    if (!values.route_date) {
      message.error(t("route.route_date_required", "La fecha de ruta es obligatoria"));
      return;
    }
    if (!values.auditor) {
      message.error(t("route.auditor_required", "Selecciona un auditor"));
      return;
    }
    setSubmitting(true);
    updateRoute(
      {
        resource: "routes",
        id: route.id,
        values: {
          route_date: dayjs(values.route_date).format("YYYY-MM-DD"),
          auditor: values.auditor,
        },
      },
      {
        onSuccess: () => {
          setSubmitting(false);
          message.success(t("route.update_success", "Ruta actualizada"));
          onClose();
          onSuccess?.();
        },
        onError: (error: any) => {
          setSubmitting(false);
          const detail =
            error?.originalError?.response?.data?.detail
            || error?.response?.data?.detail
            || error?.message;
          message.error(detail || t("route.update_error", "No se pudo actualizar la ruta"));
        },
      },
    );
  };

  return (
    <Modal
      title={t("route.edit", "Editar ruta")}
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save", "Guardar")}
      cancelText={t("common.cancel", "Cancelar")}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item
          name="route_date"
          label={t("route.route_date", "Fecha de ruta")}
          rules={[
            {
              required: true,
              message: `${t("route.route_date", "Fecha de ruta")} ${t("common.required", "es obligatorio")}`,
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={t("route.route_date_placeholder", "Selecciona la fecha")}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
          />
        </Form.Item>

        <Form.Item
          name="auditor"
          label={t("route.auditor", "Auditor")}
          rules={[
            {
              required: true,
              message: `${t("route.auditor", "Auditor")} ${t("common.required", "es obligatorio")}`,
            },
          ]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t("common.select", "Selecciona")}
            loading={auditorPicker.loading}
            disabled={auditorPicker.locked}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            options={auditorPicker.options}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
